import { TRPCErrorBoundary } from '@client/components/_core/error-handling/TRPCErrorBoundary';
import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { DndTable } from '@client/components/_ui/data-display/DndTable';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { TaggedList } from '@client/components/_ui/display/TaggedList';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { ComplaintFlagTag } from '@client/components/complaints/ComplaintFlagTag';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useIsLiteOrg } from '@client/hooks/business/org/useIsLiteOrg';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { useAntdTable } from '@client/hooks/table';
import { type RouterOutputs, trpc } from '@client/trpc/client';
import type { TableColumnType } from '@client/types/common';
import { DEFAULT_ACTION_COLUMN } from '@client/utils/ui/table';
import { COMPLAINT_FLAG_TAG_LABELS } from '@shared/constants/business/complaint-flag';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { Switch, Tag } from 'antd';
import { Flag } from 'lucide-react';
import { useState } from 'react';

export const Route = createFileRoute('/console/$orgId/configuration/compliance/complaints/flags')({
    staticData: {
        metadata: {
            title: 'Flags',
        },
        ui: {
            icon: Flag,
            title: 'Complaint flags',
            subtitle: 'Manage the secondary flags that can be applied to complaints',
            menuDescription: 'Manage the secondary flags that can be applied to complaints',
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ComplaintFlagsSearch />
        </PageController>
    );
}

// Drag-and-drop keys must be string/number, so `id` is mapped to a string for the table
// and converted back to bigint at each mutation boundary.
type FlagRow = Omit<NonNullable<RouterOutputs['complaintFlag']['listComplaintFlags']>['rows'][number], 'id'> & {
    id: string;
};

function ComplaintFlagsSearch() {
    const orgId = useOrgId();
    const { trpcUtils } = useTrpc();
    const navigate = useNavigate();

    // Curium Lite (CUR-379): template settings are read-only — mutating affordances hidden; the
    // server refuses the mutations regardless.
    const isLiteOrg = useIsLiteOrg();

    // A flag is disabled/re-enabled via the "Enable flag" toggle in the edit form
    // (`isActive`). Disabled flags are hidden from the list by default and revealed by
    // this toggle so they can be reopened and re-enabled.
    const [showDisabled, setShowDisabled] = useState(false);

    const { data, isPending, error } = trpc.complaintFlag.listComplaintFlags.useQuery(
        { where: showDisabled ? undefined : { isActive: true } },
        {
            select(data) {
                return { rows: data.rows.map(({ id, ...row }) => ({ ...row, id: String(id) })) };
            },
        },
    );

    const { mutate: deleteFlag } = trpc.complaintFlag.deleteComplaintFlag.useMutation();
    const { mutate: reorderFlags } = trpc.complaintFlag.reorderComplaintFlags.useMutation();

    const { tableProps } = useAntdTable<FlagRow>({
        rowKey: 'id',
        data: {
            loading: isPending,
            rows: data?.rows,
            total: data?.rows?.length,
        },
        columns: [
            {
                title: 'Name',
                dataIndex: 'name',
                render(_, row) {
                    return (
                        <ComplaintFlagTag
                            data={{ name: row.name, type: 'SECONDARY', displayColor: row.displayColor, tags: row.tags }}
                        />
                    );
                },
            },
            {
                title: 'Tags',
                dataIndex: 'tags',
                render(_, row) {
                    return (
                        <TaggedList items={row.tags} accessor={(tag) => COMPLAINT_FLAG_TAG_LABELS[tag]} emptyText="—" />
                    );
                },
            },
            {
                title: 'Status',
                dataIndex: 'isActive',
                width: 100,
                render(_, row) {
                    return (
                        <Tag variant="outlined" color={row.isActive ? 'green' : 'default'}>
                            {row.isActive ? 'Active' : 'Disabled'}
                        </Tag>
                    );
                },
            },
            ...(isLiteOrg
                ? []
                : [
                      {
                          render(_, row) {
                              return (
                                  <Button.Delete
                                      onConfirm={(onSuccess) =>
                                          deleteFlag(
                                              { id: BigInt(row.id) },
                                              {
                                                  onSuccess() {
                                                      onSuccess();
                                                      void trpcUtils.complaintFlag.invalidate();
                                                  },
                                                  onError(error) {
                                                      errorMessage.show(error);
                                                  },
                                              },
                                          )
                                      }
                                  />
                              );
                          },
                          ...DEFAULT_ACTION_COLUMN,
                      } as TableColumnType<FlagRow>,
                  ]),
        ],
    });

    return (
        <TRPCErrorBoundary error={error}>
            <Outlet />
            <TableToolbar
                filters={
                    <Switch
                        checked={showDisabled}
                        onChange={setShowDisabled}
                        checkedChildren="Hide disabled"
                        unCheckedChildren="Show disabled"
                    />
                }
                actions={
                    !isLiteOrg && (
                        <Button.Add.Link
                            to="/console/$orgId/configuration/compliance/complaints/flags/add"
                            params={{ orgId }}
                            variant="primary"
                            label="New flag"
                        />
                    )
                }
            />
            {/* When lite, the drag-reorder handles (the only buttons in the first column) are hidden */}
            <div className={isLiteOrg ? '[&_td:first-child_button]:hidden' : undefined}>
                <DndTable
                    size="small"
                    onDragEnd={(keys) =>
                        !isLiteOrg &&
                        reorderFlags(
                            keys.map((id, index) => ({ id: BigInt(id), order: index + 1 })),
                            {
                                onSuccess() {
                                    void trpcUtils.complaintFlag.invalidate();
                                },
                                onError(error) {
                                    message.destroy();
                                    errorMessage.show(error);
                                },
                            },
                        )
                    }
                    onRow={
                        isLiteOrg
                            ? undefined
                            : ({ id }) => ({
                                  className: 'cursor-pointer',
                                  onClick(e) {
                                      navigate(
                                          {
                                              to: '/console/$orgId/configuration/compliance/complaints/flags/edit/$complaintFlagId',
                                              params: { orgId, complaintFlagId: BigInt(id) },
                                          },
                                          e,
                                      );
                                  },
                              })
                    }
                    {...tableProps}
                />
            </div>
        </TRPCErrorBoundary>
    );
}
